/**
 * Progressive Disclosure Hook for Community Onboarding
 * Handles interest detection, tier mapping, and conditional questioning logic
 */

import { useState, useCallback, useEffect } from 'react';
import {
  INTEREST_OPTIONS,
  CONDITIONAL_QUESTIONS,
  TIER_QUESTION_LABELS,
} from '../../constants/onboarding/progressiveDisclosure';
import type { TierType } from '../../constants/onboarding/tierConfiguration';
import type {
  UseProgressiveDisclosureReturn,
  ConditionalQuestionData,
} from '../../types/onboarding';

export const useProgressiveDisclosure = (): UseProgressiveDisclosureReturn => {
  const [detectedInterests, setDetectedInterests] = useState<string[]>([]);
  const [recommendedTier, setRecommendedTier] = useState<TierType | null>(null);

  const handleInterestSelection = useCallback((interestId: string, checked: boolean) => {
    setDetectedInterests((prev) => {
      if (checked) {
        return [...prev, interestId];
      } else {
        return prev.filter((id) => id !== interestId);
      }
    });
  }, []);

  const analyzeAndRecommendTier = useCallback((): TierType => {
    const silverScore = detectedInterests.filter(
      (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'silver',
    ).length;

    const goldScore = detectedInterests.filter(
      (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'gold',
    ).length;

    const customScore = detectedInterests.filter(
      (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'custom',
    ).length;

    // Enhanced scoring with weights and considerations
    let tierScores = {
      silver: silverScore * 1.0,
      gold: goldScore * 1.2, // Slightly weighted towards gold for mid-tier needs
      custom: customScore * 2.0, // Heavily weighted - any custom interest should push towards custom
    };

    // Determine recommended tier based on highest weighted score
    let recommendedTier: TierType;

    // Custom tier if any custom interests OR high combined score
    if (tierScores.custom > 0 || silverScore + goldScore >= 5) {
      recommendedTier = 'custom';
    }
    // Gold tier if gold score is higher OR mixed high scores
    else if (tierScores.gold > tierScores.silver || (goldScore >= 2 && silverScore >= 2)) {
      recommendedTier = 'gold';
    }
    // Silver tier if any silver interests OR default
    else if (tierScores.silver > 0) {
      recommendedTier = 'silver';
    }
    // Default fallback
    else {
      recommendedTier = 'silver';
    }

    // Update the recommended tier state
    setRecommendedTier(recommendedTier);

    return recommendedTier;
  }, [detectedInterests]);

  // Auto-update recommendation when interests change
  useEffect(() => {
    if (detectedInterests.length > 0) {
      analyzeAndRecommendTier();
    } else {
      setRecommendedTier('silver'); // Default when no interests selected
    }
  }, [detectedInterests]); // Remove analyzeAndRecommendTier from deps to avoid infinite loops

  const getConditionalQuestions = useCallback((): ConditionalQuestionData => {
    const detectedTiers = Array.from(
      new Set(
        detectedInterests
          .map((interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier)
          .filter(Boolean),
      ),
    ) as TierType[];

    const questions: ConditionalQuestionData = {};

    detectedTiers.forEach((tier) => {
      if (CONDITIONAL_QUESTIONS[tier]) {
        questions[tier] = CONDITIONAL_QUESTIONS[tier];
      }
    });

    return questions;
  }, [detectedInterests]);

  const getRecommendationReasoning = useCallback(() => {
    const silverScore = detectedInterests.filter(
      (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'silver',
    ).length;

    const goldScore = detectedInterests.filter(
      (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'gold',
    ).length;

    const customScore = detectedInterests.filter(
      (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'custom',
    ).length;

    const totalScore = silverScore + goldScore + customScore;
    const factors: string[] = [];

    if (silverScore > 0) factors.push(`${silverScore} silver-tier interests`);
    if (goldScore > 0) factors.push(`${goldScore} gold-tier interests`);
    if (customScore > 0) factors.push(`${customScore} custom-tier interests`);

    return {
      score: Math.round((totalScore / detectedInterests.length) * 100) || 0,
      factors,
      interestAnalysis: { silverScore, goldScore, customScore },
    };
  }, [detectedInterests]);

  return {
    detectedInterests,
    recommendedTier,
    handleInterestSelection,
    analyzeAndRecommendTier,
    getConditionalQuestions,
    getRecommendationReasoning,
  };
};
